import {
  type Table,
  type TableMeta,
  useReactTable,
} from '@tanstack/react-table';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useLotSizingSupplierLines } from '@/features/lot-sizing/hooks/use-lot-sizing-supplier-lines';
import { useLotSizingPurchaseOrderDraft } from '@/features/lot-sizing/lot-sizing-purchase-order-draft-context';
import { useUpdateLotSizingLine } from '@/services/apis/mutations/lot-sizing/use-update-lot-sizing-line';
import { useUpdateLotSizingSupplierLinesChecked } from '@/services/apis/mutations/lot-sizing/use-update-lot-sizing-supplier-lines-checked';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';
import type { Supplier } from '@/types/core/purchase-planning/supplier';

export function useLotSizingSupplierTable(
  masterTable: Table<LotSizingLine>,
  lotSizingId: string,
  supplier: Supplier,
  hasRelatedPurchaseOrders: boolean,
) {
  const supplierId = String(supplier.id);
  const { items, total, isEligibleToUpdate, hasMore, isFetching, loadMoreRef } =
    useLotSizingSupplierLines({
      lotSizingId,
      supplierId,
    });
  const isActionsDisabled = !isEligibleToUpdate;
  const { getAddedLines, getDisplayLine, setLineChecked, setLineQuantity } =
    useLotSizingPurchaseOrderDraft();
  const draftAddedLines = useMemo(
    () => getAddedLines(supplier.id),
    [getAddedLines, supplier.id],
  );

  const displayItems = useMemo(
    () =>
      hasRelatedPurchaseOrders
        ? [...items.map((item) => getDisplayLine(item)), ...draftAddedLines]
        : items,
    [draftAddedLines, getDisplayLine, hasRelatedPurchaseOrders, items],
  );

  const rowSelection = useMemo(
    () =>
      Object.fromEntries(
        displayItems.map((item) => [`${item.id}`, item.checked]),
      ),
    [displayItems],
  );
  const { mutate: updateLineMutation } = useUpdateLotSizingLine();
  const { mutate: updateSupplierLinesCheckedMutation } =
    useUpdateLotSizingSupplierLinesChecked();
  const [isBulkCheckedLocked, setIsBulkCheckedLocked] = useState(false);

  const debouncedQuantityChange = useMemo(
    () =>
      debounce(
        (rowId: string, quantity: number) =>
          updateLineMutation({
            lotSizingId,
            supplierId,
            lineId: rowId,
            quantity,
          }),
        1000,
      ),
    [lotSizingId, supplierId, updateLineMutation],
  );
  useEffect(
    () => () => debouncedQuantityChange.cancel(),
    [debouncedQuantityChange],
  );

  const table = useReactTable({
    ...masterTable.options,
    state: {
      ...masterTable.options.state,
      rowSelection,
    },
    enableRowSelection: !isActionsDisabled,
    onRowSelectionChange: () => null,
    data: displayItems,
    meta: {
      hasRelatedPurchaseOrders,
      onQuantityChange: isActionsDisabled
        ? undefined
        : (rowId: string, quantity: number) => {
            if (hasRelatedPurchaseOrders) {
              const line = displayItems.find(
                (item) => String(item.id) === rowId,
              );

              if (line !== undefined) {
                setLineQuantity(line, quantity);
              }

              return;
            }

            debouncedQuantityChange(rowId, quantity);
          },
      onCheckedChange: isActionsDisabled
        ? undefined
        : (rowId: string, checked: boolean) => {
            if (hasRelatedPurchaseOrders) {
              const line = displayItems.find(
                (item) => String(item.id) === rowId,
              );

              if (line !== undefined) {
                setLineChecked(line, checked);
              }

              return;
            }

            updateLineMutation({
              lotSizingId,
              supplierId,
              lineId: rowId,
              checked,
            });
          },
      onBulkCheckedChange:
        hasRelatedPurchaseOrders || isActionsDisabled
          ? undefined
          : (checked: boolean) => {
              if (isBulkCheckedLocked) {
                return;
              }
              setIsBulkCheckedLocked(true);
              updateSupplierLinesCheckedMutation(
                {
                  lotSizingId,
                  supplierId,
                  checked,
                },
                {
                  onSettled: () => setIsBulkCheckedLocked(false),
                },
              );
            },
      isActionsDisabled,
      isBulkCheckedLocked:
        hasRelatedPurchaseOrders || isActionsDisabled
          ? false
          : isBulkCheckedLocked,
    } as TableMeta<LotSizingLine>,
  });

  return {
    table,
    total: total + draftAddedLines.length,
    hasMore,
    isFetching,
    loadMoreRef,
  };
}
